/**
 * ── LIBRARY ORIGIN SELECTION — `?assets=` in dev, ONE answer in production (plan §4.5) ───────────
 *
 * The `resolveAxonSelection` pattern (`net/axon/gate.ts`): a pure fold of `(search, dev, env)` →
 * `{ origin, verify, warnings }`, so that "where do library bytes come from" is a golden and not a
 * branch inside a fetch. Production has exactly one answer — the per-environment config's library
 * origin, else same-origin (`''`) — and NEVER reads the URL: a player cannot point the runtime at
 * another host by typing a query string. Dev accepts:
 *
 *   ?assets=local            same-origin (`public/library/**`, the dev default)
 *   ?assets=cdn              the configured origin (`envLibraryOrigin`); warns + local when none
 *   ?assets=https://host     that origin verbatim (http/https only)
 *   ?assets=verify           same-origin AND per-load sha256 verification (§2.2)
 *   ?assets=cdn,verify       comma-joined: the last origin word wins, `verify` is a flag
 *
 * Unknown words WARN (as data — the service prints once) and fall back to local. Never a
 * wholesale `import.meta.env`: the caller passes the ONE named key it read (the credential-leak
 * rule in CLAUDE.md).
 *
 * PURE (`PURE_DIRS`): strings in, a record out. `URLSearchParams`/`URL` are ES globals, not DOM.
 */

export type LibraryOriginMode = 'local' | 'cdn' | 'url';

export interface LibraryOriginSelection {
  /** `''` = same-origin; otherwise `https://host[:port]` with no trailing slash. */
  origin: string;
  /** Per-load sha256 verification (dev only — `?assets=verify`). Always `false` in production. */
  verify: boolean;
  mode: LibraryOriginMode;
  /** Sentences the service warns ONCE; empty on every well-formed input. */
  warnings: string[];
}

const stripSlash = (s: string): string => (s.endsWith('/') ? s.slice(0, -1) : s);

/** An absolute http(s) URL → its origin, else `null`. Path/query/hash are dropped, deliberately. */
export function parseOriginUrl(value: string): string | null {
  if (!/^https?:\/\//i.test(value)) return null;
  try {
    const u = new URL(value);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.origin;
  } catch {
    return null;
  }
}

export function resolveLibraryOrigin(
  search: string,
  dev: boolean,
  envLibraryOrigin?: string | null,
): LibraryOriginSelection {
  const env = envLibraryOrigin === undefined || envLibraryOrigin === null || envLibraryOrigin === '' ? null : stripSlash(envLibraryOrigin);
  if (!dev) {
    return env === null
      ? { origin: '', verify: false, mode: 'local', warnings: [] }
      : { origin: env, verify: false, mode: 'cdn', warnings: [] };
  }

  const params = new URLSearchParams(search);
  const warnings: string[] = [];
  let origin = '';
  let mode: LibraryOriginMode = 'local';
  let verify = false;

  const raw = params.get('assets');
  if (raw === null || raw === '') return { origin, verify, mode, warnings };

  for (const word of raw.split(',').map((w) => w.trim()).filter((w) => w !== '')) {
    if (word === 'local') {
      origin = '';
      mode = 'local';
    } else if (word === 'verify') {
      verify = true;
    } else if (word === 'cdn') {
      if (env === null) {
        warnings.push(`?assets=cdn but no library origin is configured — using same-origin (local).`);
        origin = '';
        mode = 'local';
      } else {
        origin = env;
        mode = 'cdn';
      }
    } else {
      const parsed = parseOriginUrl(word);
      if (parsed === null) {
        warnings.push(
          `unknown ?assets='${word}' — using same-origin (local). ` +
            `Use local | cdn | verify | an http(s) origin.`,
        );
      } else {
        origin = parsed;
        mode = 'url';
      }
    }
  }
  return { origin, verify, mode, warnings };
}
